/* PROLURE — theme.js (no dependencies) */
(() => {
  'use strict';

  /* ---------------------------------------------------------------------
     Header offset (used by hero height + sticky product info)
  --------------------------------------------------------------------- */
  const setHeaderOffset = () => {
    const group = document.querySelectorAll('.shopify-section-group-header-group');
    let h = 0;
    group.forEach((el) => { h += el.offsetHeight; });
    if (!h) {
      const hdr = document.querySelector('.header-wrapper');
      const ab = document.querySelector('.announcement');
      h = (hdr ? hdr.offsetHeight : 0) + (ab ? ab.offsetHeight : 0);
    }
    document.documentElement.style.setProperty('--header-offset', `${h}px`);
  };
  window.addEventListener('resize', setHeaderOffset, { passive: true });

  /* ---------------------------------------------------------------------
     Dynamic header: transparent over the home banner -> solid on scroll,
     hides while scrolling down, returns when scrolling up.
  --------------------------------------------------------------------- */
  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const root = document.documentElement;
    const hideOnScroll = header.dataset.hideOnScroll === 'true';
    const transparent = header.classList.contains('is-transparent');
    const ab = document.querySelector('.section-announcement');
    let lastY = window.scrollY;
    let ticking = false;

    const measure = () => {
      root.style.setProperty('--header-h', `${header.offsetHeight}px`);
      root.style.setProperty('--announcement-h', `${ab ? ab.offsetHeight : 0}px`);
    };

    const update = () => {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      const start = ab ? ab.offsetHeight : 0;
      header.classList.toggle('is-solid', !transparent || y > start + 8);
      header.classList.toggle('is-scrolled', y > start + 8);
      if (hideOnScroll && !document.body.classList.contains('drawer-open')) {
        const goingDown = y > lastY;
        const pastHeader = y > start + header.offsetHeight * 2;
        if (goingDown && pastHeader && Math.abs(y - lastY) > 4) header.classList.add('is-hidden');
        else if (!goingDown && Math.abs(y - lastY) > 4) header.classList.remove('is-hidden');
        if (y <= start) header.classList.remove('is-hidden');
      }
      root.classList.toggle('header-hidden', header.classList.contains('is-hidden'));
      lastY = y;
    };

    measure();
    update();
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    // keyboard users: always reveal header when focus moves into it
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
  };
  document.addEventListener('DOMContentLoaded', initHeader);
  document.addEventListener('shopify:section:load', initHeader);
  document.addEventListener('DOMContentLoaded', setHeaderOffset);
  document.addEventListener('shopify:section:load', setHeaderOffset);

  /* ---------------------------------------------------------------------
     Countdown timer <countdown-timer>
     data-date="2026-10-31" data-time="20:00" data-offset="-05:00"
  --------------------------------------------------------------------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const date = (this.dataset.date || '').trim();
      const time = (this.dataset.time || '00:00').trim().padStart(5, '0');
      const offset = (this.dataset.offset || 'Z').trim();
      const iso = `${date}T${time.length === 5 ? `${time}:00` : time}${offset === '+00:00' ? 'Z' : offset}`;
      this.end = Date.parse(iso);
      if (Number.isNaN(this.end)) {
        this.end = Date.parse(`${date} ${time}`);
      }
      if (Number.isNaN(this.end)) {
        this.hidden = true;
        return;
      }
      this.units = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };
      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const diff = this.end - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        this.dataset.state = 'expired';
        if (this.dataset.hideOnExpire === 'true') {
          const bar = this.closest('[data-announcement]');
          if (bar && bar.dataset.hideBar === 'true') bar.hidden = true;
          else this.hidden = true;
          setHeaderOffset();
        }
        return;
      }
      const s = Math.floor(diff / 1000);
      const parts = {
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60,
      };
      Object.keys(parts).forEach((k) => {
        if (this.units[k]) this.units[k].textContent = String(parts[k]).padStart(2, '0');
      });
      if (this.units.days) {
        this.units.days.parentElement.hidden = parts.days === 0 && this.dataset.hideZeroDays === 'true';
      }
      this.dataset.state = 'running';
    }
  }
  if (!customElements.get('countdown-timer')) customElements.define('countdown-timer', CountdownTimer);

  /* ---------------------------------------------------------------------
     Menu drawer <menu-drawer>
  --------------------------------------------------------------------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
      this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('.drawer__expand').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = document.getElementById(btn.getAttribute('aria-controls'));
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          if (sub) sub.hidden = open;
        });
      });
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((btn) => {
        btn.addEventListener('click', () => this.open(btn));
      });
    }

    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('drawer-open');
      document.addEventListener('keydown', this.onKey);
      const first = this.querySelector('[data-drawer-close].drawer__close');
      setTimeout(() => first && first.focus(), 50);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus();
      }
      document.body.classList.remove('drawer-open');
      document.removeEventListener('keydown', this.onKey);
    }
  }
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---------------------------------------------------------------------
     Hero video: fall back to image if the video fails / reduced motion
  --------------------------------------------------------------------- */
  class HeroMedia extends HTMLElement {
    connectedCallback() {
      const video = this.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.removeAttribute('controls');
      const hide = () => video.classList.add('is-hidden');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        video.pause();
        hide();
        return;
      }
      video.addEventListener('error', hide, true);
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }
  if (!customElements.get('hero-media')) customElements.define('hero-media', HeroMedia);

  /* ---------------------------------------------------------------------
     Product row slider <product-row>
  --------------------------------------------------------------------- */
  class ProductRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.product-row__track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.bar = this.querySelector('.product-row__progress span');
      const step = () => {
        const item = this.track.querySelector('li');
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : 300;
      };
      if (this.prev) this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (this.next) this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update, { passive: true });
      this.update();
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      if (this.prev) this.prev.disabled = scrollLeft <= 2;
      if (this.next) this.next.disabled = scrollLeft >= max - 2;
      const nav = this.querySelector('.product-row__nav');
      if (nav) nav.style.visibility = max <= 2 ? 'hidden' : '';
      if (this.bar) {
        const ratio = scrollWidth ? clientWidth / scrollWidth : 1;
        const w = Math.min(100, ratio * 100);
        const x = max > 0 ? (scrollLeft / max) * (100 / w * 100 - 100) : 0;
        this.bar.parentElement.hidden = ratio >= 0.999;
        this.bar.style.setProperty('--progress-w', `${w}%`);
        this.bar.style.setProperty('--progress-x', `${x}%`);
      }
    }
  }
  if (!customElements.get('product-row')) customElements.define('product-row', ProductRow);

  /* ---------------------------------------------------------------------
     Product form <product-form>: option buttons -> variant id
  --------------------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.idInput = this.querySelector('input[name="id"]');
      this.button = this.querySelector('[data-add-to-cart]');
      this.buttonText = this.querySelector('[data-add-to-cart-text]');
      this.priceEl = this.closest('.product__info')?.querySelector('[data-price-container]');
      this.money = this.dataset.moneyFormat || '${{amount}}';
      this.querySelectorAll('.option input').forEach((input) => input.addEventListener('change', () => this.onChange()));
      this.querySelectorAll('[data-qty]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = this.querySelector('input[name="quantity"]');
          const val = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qty, 10));
          input.value = val;
        });
      });
      this.updateAvailability();
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('.option')).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.querySelectorAll('.option').forEach((fs, i) => {
        const legendValue = fs.querySelector('[data-selected-value]');
        if (legendValue) legendValue.textContent = selected[i] || '';
      });
      this.updateAvailability();
      if (!variant) {
        this.setButton(false, this.button.dataset.unavailable || 'Unavailable');
        return;
      }
      this.idInput.value = variant.id;
      const skuEl = document.querySelector('[data-sku]');
      if (skuEl) skuEl.textContent = variant.sku || '';
      this.setButton(variant.available, variant.available ? this.button.dataset.add : this.button.dataset.soldout);
      this.updatePrice(variant);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      if (variant.featured_media) {
        const media = document.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (media && window.matchMedia('(max-width: 989px)').matches) {
          media.parentElement.scrollTo({ left: media.offsetLeft - 20, behavior: 'smooth' });
        }
      }
    }

    updateAvailability() {
      // strike through values that have no available variant given the other selections
      const selected = this.selectedOptions();
      this.querySelectorAll('.option').forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const available = this.variants.some((v) => v.available && v.options[index] === input.value
            && v.options.every((opt, i) => i === index || selected[i] === null || opt === selected[i]));
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }

    setButton(enabled, text) {
      if (!this.button) return;
      if (this.button.hasAttribute('data-preorder-closed')) {
        enabled = false;
        text = this.button.dataset.closed || text;
      }
      this.button.disabled = !enabled;
      if (this.buttonText && text) this.buttonText.textContent = text;
    }

    formatMoney(cents) {
      const value = (cents / 100);
      const fmt = this.money;
      const withDelims = (n, dec, thou, sep) => {
        const [i, d] = n.toFixed(dec).split('.');
        return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (d ? sep + d : '');
      };
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
          case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
          default: return withDelims(value, 2, ',', '.');
        }
      });
    }

    updatePrice(variant) {
      if (!this.priceEl) return;
      const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
      let html = `<div class="price${sale ? ' price--sale' : ''}"><span class="price__current">${this.formatMoney(variant.price)}</span>`;
      if (sale) html += `<s class="price__compare">${this.formatMoney(variant.compare_at_price)}</s>`;
      if (!variant.available) html += '<span class="price__badge">Sold out</span>';
      html += '</div>';
      this.priceEl.innerHTML = html;
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------------------------------------------------------------------
     Product gallery counter (mobile swipe) + "Size guide" opens its tab
  --------------------------------------------------------------------- */
  const initProductPage = () => {
    document.querySelectorAll('[data-gallery]').forEach((g) => {
      const out = g.parentElement.querySelector('[data-gallery-current]');
      if (!out) return;
      g.addEventListener('scroll', () => {
        const w = g.firstElementChild ? g.firstElementChild.getBoundingClientRect().width : g.clientWidth;
        const i = Math.round(g.scrollLeft / Math.max(1, w)) + 1;
        out.textContent = String(i).padStart(2, '0');
      }, { passive: true });
    });
  };
  document.addEventListener('DOMContentLoaded', initProductPage);
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-open-accordion]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    target.open = true;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ---------------------------------------------------------------------
     Pre-order: switch off at the scheduled time, live, without a reload.
     The server also checks the time, this handles cached pages and
     visitors who keep the page open past the deadline.
  --------------------------------------------------------------------- */
  const preorderEnd = (el) => {
    const date = (el.dataset.date || '').trim();
    if (!date) return NaN;
    let time = (el.dataset.time || '00:00').trim();
    if (time.length === 4) time = `0${time}`;
    const off = (el.dataset.offset || '+00:00').trim();
    return Date.parse(`${date}T${time}:00${off === '+00:00' ? 'Z' : off}`);
  };
  const atTime = (end, fn) => {
    const wait = end - Date.now();
    if (wait <= 0) { fn(); return; }
    setTimeout(() => atTime(end, fn), Math.min(wait, 2147483000));
  };
  const initPreorder = () => {
    document.querySelectorAll('[data-preorder]').forEach((note) => {
      const end = preorderEnd(note);
      if (Number.isNaN(end)) return;
      atTime(end, () => {
        const form = note.closest('product-form') || document;
        const btn = form.querySelector('[data-add-to-cart]');
        const txt = form.querySelector('[data-add-to-cart-text]');
        form.querySelectorAll('[data-preorder-input]').forEach((i) => i.remove());
        const closed = note.dataset.after === 'closed';
        if (btn) {
          const wasPreorderLabel = txt && txt.textContent.trim() === (btn.dataset.add || '').trim();
          if (closed) {
            btn.setAttribute('data-preorder-closed', '');
            btn.dataset.closed = note.dataset.closedLabel || 'Closed';
            btn.disabled = true;
            if (txt) txt.textContent = btn.dataset.closed;
          } else {
            btn.dataset.add = btn.dataset.addDefault || 'Add to cart';
            if (txt && wasPreorderLabel) txt.textContent = btn.dataset.add;
          }
        }
        const cd = note.querySelector('countdown-timer');
        if (cd) cd.remove();
        if (closed) {
          note.classList.add('is-closed');
          const l = note.querySelector('[data-preorder-label]');
          const t = note.querySelector('[data-preorder-text]');
          if (l) l.textContent = note.dataset.closedLabel || '';
          if (t) t.innerHTML = `<p>${note.dataset.closedNote || ''}</p>`;
        } else {
          note.hidden = true;
        }
      });
    });
    document.querySelectorAll('[data-preorder-badge]').forEach((b) => {
      const end = preorderEnd(b);
      if (!Number.isNaN(end)) atTime(end, () => b.remove());
    });
  };
  document.addEventListener('DOMContentLoaded', initPreorder);
  document.addEventListener('shopify:section:load', initPreorder);

  /* ---------------------------------------------------------------------
     Product recommendations (loaded after page render)
  --------------------------------------------------------------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded || !this.dataset.url) return;
      this.dataset.loaded = 'true';
      fetch(this.dataset.url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim().length) this.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------------------------------------------------------------------
     Cart: auto-submit sort select / quantity update
  --------------------------------------------------------------------- */
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (el.matches('[data-sort-select]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', el.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }
    if (el.matches('[data-cart-qty]')) {
      const form = el.closest('form');
      if (form) {
        const btn = form.querySelector('[name="update"]');
        if (btn) btn.click(); else form.submit();
      }
    }
  });

  /* ---------------------------------------------------------------------
     Size recommender: pure matching logic (unit-tested)
  --------------------------------------------------------------------- */
  /* Pure logic for the PROLURE size recommender. No DOM. */

  function srNum(raw) {
    if (raw == null) return null;
    var str = String(raw);
    var found = str.match(/\d+(?:\.\d+)?/g);
    if (!found) return null;
    if (found.length >= 2 && /[-–—]/.test(str)) {
      return (parseFloat(found[0]) + parseFloat(found[1])) / 2;   // "44-46" -> 45
    }
    return parseFloat(found[0]);
  }

  function srLooksLikeSize(v) {
    var s = String(v == null ? '' : v).trim().replace(/\s+/g, '');
    return /^(xxs|xs|s|m|l|xl|xxl|xxxl|[2-5]xl|\d{1,2})$/i.test(s);
  }

  /** rows: array of arrays of cell text, header first. Handles both orientations. */
  function srParseChart(rows) {
    if (!rows || rows.length < 2) return null;
    var grid = rows.map(function (r) { return r.map(function (c) { return String(c == null ? '' : c).trim(); }); });

    // Sizes across the top instead of down the side? Transpose.
    var head = grid[0];
    var sizeish = 0;
    for (var i = 1; i < head.length; i++) if (srLooksLikeSize(head[i])) sizeish++;
    if (sizeish >= 2) {
      var t = [];
      for (var c = 0; c < head.length; c++) {
        t.push(grid.map(function (row) { return row[c] == null ? '' : row[c]; }));
      }
      grid = t;
    }

    var labels = grid[0].map(function (h) { return h.toLowerCase(); });
    var out = {};
    for (var r = 1; r < grid.length; r++) {
      var key = grid[r][0];
      if (!key) continue;
      var rec = {};
      for (var k = 1; k < grid[r].length && k < labels.length; k++) {
        var v = srNum(grid[r][k]);
        if (v != null) rec[labels[k]] = v;
      }
      if (Object.keys(rec).length) out[key.toUpperCase().replace(/\s+/g, '')] = rec;
    }
    return Object.keys(out).length ? out : null;
  }

  function srParseMatrix(text) {
    if (!text) return [];
    return String(text).split('\n').map(function (line) {
      var p = line.split(',').map(function (x) { return x.trim(); });
      if (p.length < 5) return null;
      var row = { size: p[0], hMin: parseFloat(p[1]), hMax: parseFloat(p[2]), wMin: parseFloat(p[3]), wMax: parseFloat(p[4]) };
      var ok = row.size && [row.hMin, row.hMax, row.wMin, row.wMax].every(function (n) { return isFinite(n); });
      return ok ? row : null;
    }).filter(Boolean);
  }

  function srFindKey(rec, re) {
    var keys = Object.keys(rec || {});
    for (var i = 0; i < keys.length; i++) if (re.test(keys[i])) return keys[i];
    return null;
  }

  /** Charts quote either pit-to-pit (half) or full circumference. */
  function srCircumference(v) { return v < 32 ? v * 2 : v; }

  function srNorm(s) { return String(s == null ? '' : s).toUpperCase().replace(/\s+/g, ''); }

  /** Smallest offered size whose garment measurement clears body + ease. */
  function srByChart(sizes, chart, bodyIn, ease, re) {
    if (!chart || !(bodyIn > 0)) return null;
    var ordered = sizes.filter(function (s) { return chart[srNorm(s)]; });
    if (!ordered.length) return null;

    var target = bodyIn + ease;
    var scored = ordered.map(function (s) {
      var rec = chart[srNorm(s)];
      var key = srFindKey(rec, re);
      if (!key) return null;
      return { size: s, garment: srCircumference(rec[key]), label: key };
    }).filter(Boolean);
    if (!scored.length) return null;

    for (var i = 0; i < scored.length; i++) {
      if (scored[i].garment >= target) {
        return { size: scored[i].size, basis: 'chart', garment: scored[i].garment, target: target, label: scored[i].label };
      }
    }
    var last = scored[scored.length - 1];
    return { size: last.size, basis: 'chart-max', garment: last.garment, target: target, label: last.label };
  }

  function srByMatrix(sizes, matrix, heightIn, weightLb) {
    if (!matrix || !matrix.length) return null;
    if (!(heightIn > 0) && !(weightLb > 0)) return null;

    var usable = matrix.filter(function (m) {
      return sizes.some(function (s) { return srNorm(s) === srNorm(m.size); });
    });
    if (!usable.length) return null;

    // Bands overlap, so "is it inside the band" ties constantly and the tie always
    // fell to whichever size was listed first. Score distance from each band's
    // centre instead, normalised by the band's own width.
    function offCentre(v, lo, hi) {
      if (!(v > 0)) return 0;
      var centre = (lo + hi) / 2;
      var half = Math.max((hi - lo) / 2, 0.5);
      return Math.abs(v - centre) / half;
    }

    var best = null;
    usable.forEach(function (m) {
      // Weight drives fit more than height does, so it carries more of the score.
      var penalty = offCentre(heightIn, m.hMin, m.hMax) * 1.0
                  + offCentre(weightLb, m.wMin, m.wMax) * 1.5;
      if (!best || penalty < best.penalty) best = { size: m.size, penalty: penalty };
    });
    return best ? { size: best.size, basis: 'matrix', penalty: best.penalty } : null;
  }

  function srStep(sizes, size, delta) {
    var i = sizes.findIndex(function (s) { return srNorm(s) === srNorm(size); });
    if (i < 0) return size;
    var j = Math.min(sizes.length - 1, Math.max(0, i + delta));
    return sizes[j];
  }

  function srRecommend(o) {
    var sizes = o.sizes || [];
    if (!sizes.length) return null;
    var ease = o.fit === 'snug' ? o.easeSnug : o.fit === 'oversized' ? o.easeOversized : o.easeRegular;

    var byChest = srByChart(sizes, o.chart, o.chestIn, ease, /chest|bust|pit/);
    if (byChest) return byChest;

    var byWaist = srByChart(sizes, o.chart, o.waistIn, ease, /waist/);
    if (byWaist) return byWaist;

    var byBody = srByMatrix(sizes, o.matrix, o.heightIn, o.weightLb);
    if (byBody) {
      var delta = o.fit === 'oversized' ? 1 : o.fit === 'snug' ? -1 : 0;
      var stepped = srStep(sizes, byBody.size, delta);
      return { size: stepped, basis: 'matrix', stepped: stepped !== byBody.size, from: byBody.size };
    }
    return null;
  }

  /* ---------------------------------------------------------------------
     Size recommender
     Reads the product's own size chart out of the DOM and combines it with
     what the shopper tells us. Measurements win over height and weight.
  --------------------------------------------------------------------- */
  class SizeRecommender extends HTMLElement {
    connectedCallback() {
      this.cfg = this.readConfig();
      this.panel = this.querySelector('[data-sizerec-panel]');
      this.outEl = this.querySelector('[data-sizerec-out]');
      this.propEl = this.querySelector('[data-sizerec-prop]');
      this.fit = 'regular';

      const opener = this.querySelector('[data-sizerec-open]');
      if (opener) opener.addEventListener('click', () => this.toggle(opener));

      this.querySelectorAll('[data-sizerec-fit]').forEach((b) => {
        b.addEventListener('click', () => this.chooseFit(b));
      });

      const go = this.querySelector('[data-sizerec-go]');
      if (go) go.addEventListener('click', () => this.run());
    }

    readConfig() {
      const el = this.querySelector('[data-sizerec-config]');
      try { return JSON.parse(el.textContent); } catch (e) { return {}; }
    }

    toggle(opener) {
      const show = this.panel.hasAttribute('hidden');
      if (show) this.panel.removeAttribute('hidden'); else this.panel.setAttribute('hidden', '');
      opener.setAttribute('aria-expanded', show ? 'true' : 'false');
    }

    chooseFit(btn) {
      this.fit = btn.dataset.sizerecFit;
      this.querySelectorAll('[data-sizerec-fit]').forEach((b) => {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
    }

    val(sel) {
      const el = this.querySelector(sel);
      if (!el) return null;
      const n = parseFloat(el.value);
      return isFinite(n) ? n : null;
    }

    sizes() {
      const idx = this.dataset.optionIndex;
      const form = this.closest('form');
      if (!form || idx == null) return [];
      const inputs = form.querySelectorAll('input[name="option-' + idx + '"]');
      return Array.from(inputs).map((i) => i.value);
    }

    chart() {
      const sel = this.cfg.chartSelector;
      if (!sel) return null;
      let table = null;
      try { table = document.querySelector(sel + ' table'); } catch (e) { return null; }
      if (!table) return null;
      const rows = Array.from(table.rows).map((r) => Array.from(r.cells).map((c) => c.textContent));
      return srParseChart(rows);
    }

    /** Is any variant with this size still buyable? */
    available(size) {
      const form = this.closest('form');
      const node = form && form.querySelector('[data-variants-json]');
      if (!node) return null;
      let variants;
      try { variants = JSON.parse(node.textContent); } catch (e) { return null; }
      const idx = parseInt(this.dataset.optionIndex, 10);
      const want = String(size).toUpperCase().replace(/\s+/g, '');
      const matching = variants.filter((v) => {
        const o = v.options && v.options[idx];
        return o && String(o).toUpperCase().replace(/\s+/g, '') === want;
      });
      if (!matching.length) return null;
      return matching.some((v) => v.available);
    }

    selectSize(size) {
      const idx = this.dataset.optionIndex;
      const form = this.closest('form');
      if (!form) return false;
      const want = String(size).toUpperCase().replace(/\s+/g, '');
      const inputs = form.querySelectorAll('input[name="option-' + idx + '"]');
      for (const input of inputs) {
        if (String(input.value).toUpperCase().replace(/\s+/g, '') === want) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }
      }
      return false;
    }

    run() {
      const heightFt = this.val('[data-sizerec-ft]');
      const heightInch = this.val('[data-sizerec-in]');
      const heightIn = heightFt ? heightFt * 12 + (heightInch || 0) : null;

      const result = srRecommend({
        sizes: this.sizes(),
        chart: this.chart(),
        matrix: srParseMatrix(this.cfg.matrix),
        heightIn: heightIn,
        weightLb: this.val('[data-sizerec-weight]'),
        chestIn: this.val('[data-sizerec-chest]'),
        waistIn: this.val('[data-sizerec-waist]'),
        fit: this.fit,
        easeSnug: parseFloat(this.cfg.easeSnug) || 1,
        easeRegular: parseFloat(this.cfg.easeRegular) || 3,
        easeOversized: parseFloat(this.cfg.easeOversized) || 6
      });

      if (!result) {
        this.outEl.innerHTML = '<p class="sizerec__miss">Add your height and weight, or your chest measurement, and we can point you at a size.</p>';
        if (this.propEl) { this.propEl.value = ''; this.propEl.disabled = true; }
        return;
      }

      const measured = result.basis === 'chart' || result.basis === 'chart-max';
      let why = measured
        ? 'From your measurements against this piece’s own size chart.'
        : 'From your height and weight.';
      if (!measured && result.stepped) {
        why += this.fit === 'oversized'
          ? ' Sized up one for an oversized fit.'
          : ' Sized down one for a snug fit.';
      }
      if (result.basis === 'chart-max') {
        why += ' That is the largest size this piece comes in — email us before ordering.';
      }

      const stillThere = this.available(result.size);
      const soldOut = stillThere === false
        ? '<p class="sizerec__soldout">That size is sold out right now.</p>' : '';

      this.outEl.innerHTML =
        '<p class="sizerec__answer">We’d put you in <strong>' + this.escape(result.size) + '</strong></p>' +
        '<p class="sizerec__why">' + why + '</p>' + soldOut;

      if (stillThere !== false) this.selectSize(result.size);

      // Record the recommendation, not the body measurements.
      if (this.propEl && this.cfg.logToOrder) {
        this.propEl.value = result.size + ' · ' + (measured ? 'from measurements' : 'from height and weight')
                          + ' · ' + this.fit + ' fit';
        this.propEl.disabled = false;
      }
    }

    escape(str) {
      const d = document.createElement('div');
      d.textContent = String(str);
      return d.innerHTML;
    }
  }
  if (!customElements.get('size-recommender')) customElements.define('size-recommender', SizeRecommender);

})();
